export type Block =
  | { type: "h2"; text: string }
  | { type: "p"; text: string }
  | { type: "list"; items: string[] }
  | { type: "quote"; text: string };

export interface Post {
  slug: string;
  title: string;
  /** Used for <title>, kept under 60 characters. */
  metaTitle: string;
  description: string;
  excerpt: string;
  category: string;
  published: string; // ISO date
  updated?: string;
  readMinutes: number;
  relatedSlugs?: string[];
  blocks: Block[];
}

export const POSTS: Post[] = [
  {
    slug: "one-thing-a-day-business-progress",
    title: "The one-thing-a-day method for moving a business forward",
    metaTitle: "Daily Goal Tracking for Founders: The One-Thing-a-Day Method",
    description:
      "How to track business goals one day at a time: pick one move each morning, record what actually happened each evening, and read the pattern at month end.",
    excerpt:
      "Ten-item task lists measure intention. One recorded move a day measures the business. Here is the loop, the failure modes, and how to read a month of it.",
    category: "Operating systems",
    published: "2026-07-14",
    readMinutes: 7,
    relatedSlugs: ["what-to-do-when-you-miss-a-week"],
    blocks: [
      {
        type: "p",
        text: "Most founder productivity advice optimises the wrong variable. It optimises how much you plan. A business does not move because you planned ten things; it moves because one specific, externally visible action happened — a call placed, a price changed, a page shipped, an invoice sent. The one-thing-a-day method exists to record exactly that, and nothing else.",
      },
      { type: "h2", text: "How the daily loop works, in three parts" },
      {
        type: "p",
        text: "The method is a closed loop, not a list. Each part matters, and skipping the second one is the reason most daily systems quietly stop being useful after a fortnight.",
      },
      {
        type: "list",
        items: [
          "Morning intention: name one thing you will do today to move this business forward. One. Specific enough that someone else could tell whether it happened.",
          "Evening outcome: answer whether it happened and what came of it. Not a grade — a fact. \"Called, no answer, retry Thursday\" is a complete and useful entry.",
          "Monthly read: scroll the record and look for the pattern, not the score. Which category of move keeps recurring? Which keeps getting deferred?",
        ],
      },
      {
        type: "p",
        text: "The evening half is what turns a to-do list into an instrument. A plan without an outcome tells you what you hoped for. An outcome tells you what your business responds to. Over sixty days, the outcomes are the only data that matters.",
      },
      { type: "h2", text: "Why one move, not five" },
      {
        type: "p",
        text: "Constraint forces a decision. When you can only log one move, you have to rank: is today about revenue, or is it about the thing that is nagging you? Founders who write five items typically complete the two easiest and defer the one that would have mattered. The single-move constraint makes the deferral visible on the day it happens instead of at the end of the quarter.",
      },
      {
        type: "quote",
        text: "A daily record is not a performance review. It is a map of where your attention actually went.",
      },
      { type: "h2", text: "Tag the move, so the month can be read" },
      {
        type: "p",
        text: "Attach a single category to each entry — outreach, pricing, marketing, product, ops, finance. This takes two seconds and pays for itself the first time you review a month. Typical patterns founders discover in their first review:",
      },
      {
        type: "list",
        items: [
          "Eighty percent product, four percent outreach — the classic build-in-a-cave failure mode of technical founders.",
          "Heavy ops, no pricing — the business is being administered rather than grown.",
          "Outreach clustered into two frantic weeks, then nothing — feast-and-famine pipeline, visible a month before the revenue dip arrives.",
        ],
      },
      { type: "h2", text: "What to do when you miss a day" },
      {
        type: "p",
        text: "You will miss days. Travel, illness, a launch, a bad week. The method survives this only if missing a day costs nothing. Streak counters, red warnings and reset animations turn a neutral gap into a reason to abandon the tool entirely — the sunk-cost logic of a broken streak is genuinely demotivating, and it is the single most common reason daily systems get deleted.",
      },
      {
        type: "p",
        text: "Treat gaps as data. A ten-day gap in one business while another business ran hot is not a failure of discipline; it is an accurate record of where the business needed you. That is worth knowing.",
      },
      { type: "h2", text: "Running the loop across several businesses" },
      {
        type: "p",
        text: "If you run more than one thing, keep the records completely separate. Shared lists blur which business is actually moving and which is coasting on the other's energy. Separate records let you ask the question that matters at the end of a quarter: which of these deserves the next ninety days of my attention?",
      },
      {
        type: "p",
        text: "The Founders App implements this loop directly — a morning intention field, an evening outcome field, one optional tag, and a chronological history per business. No scores, no streaks, no audience.",
      },
    ],
  },
  {
    slug: "monthly-bold-move-framework",
    title: "The monthly bold move: a framework for calculated risk",
    metaTitle: "Monthly Goal Setting Framework: The Bold Move for Founders",
    description:
      "A monthly goal setting framework built on calculated risk: how to choose, size, record and review one deliberate bold move each month as a founder.",
    excerpt:
      "Consistency keeps a business alive. Asymmetric risk is what makes it grow. A monthly cadence for the move you would otherwise keep postponing.",
    category: "Strategy",
    published: "2026-07-28",
    readMinutes: 6,
    blocks: [
      {
        type: "p",
        text: "Daily discipline compounds, but it compounds along the trajectory you are already on. If the trajectory is wrong — the price is too low, the market is too small, the co-founder conversation keeps getting avoided — a year of flawless daily execution just delivers you further down the wrong road, faster.",
      },
      {
        type: "p",
        text: "The correction is a separate, slower cadence: one deliberately uncomfortable move per month, recorded and reviewed apart from the daily record.",
      },
      { type: "h2", text: "What qualifies as a monthly bold move" },
      {
        type: "p",
        text: "A bold move has three properties. It is reversible or survivable if it fails. It has genuinely asymmetric upside. And you feel resistance when you write it down — if it is comfortable, it is a task, not a risk.",
      },
      {
        type: "list",
        items: [
          "Raise prices forty percent for new customers only. Downside: slower conversion for one month. Upside: permanently repriced business.",
          "Email the twenty people you have been too intimidated to contact. Downside: silence. Upside: one relationship that changes the year.",
          "Kill the product line that consumes thirty percent of your time and produces six percent of revenue.",
          "Publish the strong opinion you have been softening for a year.",
          "Have the compensation, equity or exit conversation you have deferred twice.",
        ],
      },
      { type: "h2", text: "Why monthly is the right cadence" },
      {
        type: "p",
        text: "Weekly is too fast — real risks need time to produce a signal, and a weekly cadence degrades into a list of chores. Quarterly is too slow: four attempts a year is not enough sample size to learn anything about your own risk appetite. Monthly gives you twelve genuine experiments a year, each with roughly thirty days to resolve.",
      },
      {
        type: "quote",
        text: "Twelve calculated risks a year, honestly recorded, will teach you more about your business than any strategy document.",
      },
      { type: "h2", text: "How to size the risk" },
      {
        type: "p",
        text: "Before you commit, write the worst realistic outcome in a single sentence. If that sentence would end the business, the move is too large — reduce it until failure is a bruise rather than a fatality. Most founders discover their real fear is social (looking foolish, being rejected) rather than financial, and that fear shrinks considerably once it is written down in plain language.",
      },
      { type: "h2", text: "Record it separately, and review it honestly" },
      {
        type: "p",
        text: "Keep bold moves out of the daily stream. They operate on a different clock and deserve a different review. On the first of each month, do two things: write the new move, and write one honest sentence about what happened to the previous one — including \"I did not do it,\" which is itself a finding worth reading twelve times a year.",
      },
      {
        type: "p",
        text: "In The Founders App, the monthly prompt appears on the first of the month for each business, is stored as a distinct entry type, and sits alongside the daily record in the same timeline so you can see how the two cadences interact.",
      },
    ],
  },
  {
    slug: "running-multiple-businesses-without-losing-the-thread",
    title: "Running multiple businesses without losing the thread",
    metaTitle: "How to Run Multiple Businesses at Once and Stay Focused",
    description:
      "Running multiple businesses at once? Context switching, not workload, is what breaks operators. Separate records, review rhythms and how to allocate attention.",
    excerpt:
      "Two businesses in one tool becomes one blurred business. How to keep separate records, and how to decide which venture earns the next ninety days.",
    category: "Operating systems",
    published: "2026-08-06",
    readMinutes: 6,
    relatedSlugs: ["best-tools-solo-founders-multiple-businesses"],
    blocks: [
      {
        type: "p",
        text: "The failure mode of running several ventures is rarely hours. It is context loss: opening a business you have not touched in eleven days and spending forty minutes reconstructing where you left it. That reconstruction tax, paid several times a week, is what makes portfolio operators feel permanently behind.",
      },
      { type: "h2", text: "One record per business, never a shared list" },
      {
        type: "p",
        text: "A combined task list feels efficient and destroys the only signal that matters — which business is actually moving. When entries are pooled, a hot venture's activity masks a stalled one for months. Separate records make a stall obvious within two weeks.",
      },
      {
        type: "p",
        text: "Separation should extend past tasks: journal, contacts and history all belong to the business they concern. A contact log split across ventures is unreadable when you need to remember whether you already pitched someone, and in what capacity.",
      },
      { type: "h2", text: "Deliberate, unequal attention" },
      {
        type: "p",
        text: "Equal attention across ventures is the most common and most expensive mistake. Businesses are not symmetric: one is usually in a growth window that will close, while another can be maintained on a fraction of the effort without harm.",
      },
      {
        type: "list",
        items: [
          "Primary: daily move, every working day. One venture only.",
          "Secondary: two to three moves a week, plus the monthly bold move.",
          "Maintenance: the monthly bold move and nothing else. Deliberately parked, not neglected.",
        ],
      },
      {
        type: "p",
        text: "Write the allocation down and revisit it quarterly. An allocation you never named is not a strategy, it is a habit — and habits drift toward whichever business is most pleasant to work on rather than the one with the open window.",
      },
      { type: "h2", text: "The re-entry ritual for a parked business" },
      {
        type: "p",
        text: "When you return to a parked venture, do not open your inbox. Open its history and read the last ten entries. Two minutes of chronological record rebuilds context better than an hour of email archaeology, because the record contains your reasoning and not just other people's requests.",
      },
      {
        type: "quote",
        text: "The value of a written record is not the writing. It is the cheap re-entry three weeks later.",
      },
      { type: "h2", text: "Reviewing the portfolio" },
      {
        type: "p",
        text: "Once a quarter, read each business's timeline end to end and answer three questions per venture: what actually moved, what kept getting deferred, and would I start this today knowing what I now know? The third question is uncomfortable by design. A portfolio only works if you are willing to close something.",
      },
    ],
  },
  {
    slug: "why-streak-based-apps-backfire",
    title: "Why streak-based apps backfire for founders",
    metaTitle: "Why Streak-Based Habit and Goal Tracker Apps Backfire",
    description:
      "Streak counters in habit and goal tracker apps optimise for app engagement, not business outcomes. Why founders quit them, and what to track instead.",
    excerpt:
      "A broken streak makes people quit the tool, not work harder. Why guilt mechanics fail founders, and what a calm system measures instead.",
    category: "Behaviour",
    published: "2026-08-13",
    readMinutes: 5,
    relatedSlugs: ["what-to-do-when-you-miss-a-week"],
    blocks: [
      {
        type: "p",
        text: "Streak mechanics are borrowed from consumer engagement design, where the goal is daily app opens. For a language app, an unbroken chain is a reasonable proxy for progress — practice is the product. For a business, it is not. A founder can open an app for ninety consecutive days and build nothing.",
      },
      { type: "h2", text: "The abandonment cliff" },
      {
        type: "p",
        text: "The predictable failure is what happens after a miss. A streak converts a neutral gap into a loss, and loss aversion does not produce renewed effort in a discretionary tool — it produces avoidance. The founder skips a day, sees the counter reset, feels the sting, and stops opening the app. The record ends not because the work stopped but because the interface punished a normal week.",
      },
      {
        type: "quote",
        text: "A system you abandon after one bad week is worse than no system at all — you lose the record too.",
      },
      { type: "h2", text: "Consistency is not the outcome" },
      {
        type: "p",
        text: "Optimising for daily entry count also distorts behaviour while it lasts. When the metric is \"did you log something,\" the cheapest qualifying move wins: a tidy inbox, a tweaked landing page, an admin task. The uncomfortable, high-value move gets skipped precisely because it might not resolve inside a day.",
      },
      { type: "h2", text: "What to track instead of streaks" },
      {
        type: "list",
        items: [
          "Outcomes, not check-ins: what actually happened, in your own words.",
          "Category distribution: where attention concentrated over a month.",
          "Deferral patterns: what keeps appearing as an intention and never as an outcome.",
          "Risk cadence: whether the monthly bold move is happening at all.",
        ],
      },
      {
        type: "p",
        text: "None of these need a counter, a badge or a warning colour. They need a legible chronological record and a habit of reading it. That is a deliberately lower-stimulation product, and it is the right trade: the tool should be forgettable on a bad week and useful on a good one.",
      },
      { type: "h2", text: "Calm by design" },
      {
        type: "p",
        text: "The Founders App has no streaks, no counters and no guilt copy anywhere in the interface. Missing three days produces the same neutral timeline as missing none — with a three-day gap you can see, interpret, and act on yourself.",
      },
    ],
  },
  {
    slug: "founder-crm-contact-interaction-log",
    title: "A founder's CRM is a contact list with memory",
    metaTitle: "Founder CRM: A Simple Contact Tracker With Memory",
    description:
      "A simple founder CRM alternative: pipeline software is built for sales teams. How to run a lightweight contact tracker with interaction history that survives long gaps.",
    excerpt:
      "You do not need pipelines and deal stages. You need to remember what you said to someone eight months ago — and that is a different tool.",
    category: "Outreach",
    published: "2026-08-20",
    readMinutes: 6,
    relatedSlugs: ["best-tools-solo-founders-multiple-businesses"],
    blocks: [
      {
        type: "p",
        text: "Founders repeatedly install a full sales CRM, configure three pipelines, import contacts, and abandon it within six weeks. The tool is not badly built — it is built for a different job. Pipeline software assumes a repeatable sales motion with defined stages and a team to enforce data hygiene. An early founder has none of those things.",
      },
      { type: "h2", text: "What founders actually need from a CRM" },
      {
        type: "p",
        text: "Strip it back and the real requirement is narrow: when a name resurfaces after six months, know instantly who they are, what was said, and what you owe them. That is memory, not pipeline management.",
      },
      {
        type: "list",
        items: [
          "Who they are and how to reach them.",
          "A short standing note: how you met, what they care about, what they asked for.",
          "A dated log of every interaction, in your own words, newest last.",
        ],
      },
      { type: "h2", text: "How to write a useful contact log entry" },
      {
        type: "p",
        text: "Log entries fail when they record events instead of substance. \"Had a call\" is worthless in March. Write what would orient a stranger — yourself, months from now — in one sentence: what was discussed, what they said, and the open thread.",
      },
      {
        type: "quote",
        text: "Called 12 Feb — walked through pricing, wants a pilot for two sites in Q3, asked me to follow up after their budget cycle closes in May.",
      },
      {
        type: "p",
        text: "That single line contains the relationship, the objection, the size and the next action. It takes twenty seconds to write immediately after the call and is functionally impossible to reconstruct four months later.",
      },
      { type: "h2", text: "Log immediately or not at all" },
      {
        type: "p",
        text: "The habit only survives if the entry happens inside two minutes of the conversation, while the detail is free. Batch-logging at the end of the week produces thin, generic entries — exactly the entries that make people conclude the log is not worth keeping.",
      },
      { type: "h2", text: "Keep contacts scoped to the business" },
      {
        type: "p",
        text: "If you run several ventures, a single global address book creates a specific and awkward failure: pitching the same person twice for different companies with no memory of the first conversation. Scoping contacts to the business preserves the context of the relationship, and the same person can legitimately exist in two ventures with two separate histories.",
      },
      {
        type: "p",
        text: "The Founders App's phonebook is per business: name, phone, email, a standing note, and a dated interaction log per person that sits inside the same timeline as your daily moves.",
      },
    ],
  },
  {
    slug: "what-to-do-when-you-miss-a-week",
    title: "What to do when you miss a week of tracking",
    metaTitle: "Missed a Week of Habit Tracking? How to Restart Without Guilt",
    description:
      "Fell off your habit tracker or business goal log? Here's how to restart after a missed week without guilt, backfilling, or catch-up screens.",
    excerpt:
      "A missed week is not a streak to repair. It is information. How to re-enter your record honestly and write the next move.",
    category: "Behaviour",
    published: "2026-08-27",
    readMinutes: 5,
    blocks: [
      {
        type: "p",
        text: "Every founder who keeps any kind of record eventually opens the app and sees a gap. Not one day. A stretch. A launch swallowed the week, a family matter pulled you away, or the tool simply stopped feeling useful and you stopped opening it. The gap is normal. What determines whether the system survives is what happens next.",
      },
      { type: "h2", text: "Why catch-up mode kills the habit" },
      {
        type: "p",
        text: "Most tracking tools make a gap expensive. They show a broken streak, a red badge, a 'you missed 7 days' screen, or a prompt to backfill every empty box before you can continue. The intention is motivation. The effect is avoidance. A tool that punishes a missed week trains you to stop opening it on the eighth day. We covered the mechanics of this in more detail in the note on why streak-based apps backfire — the same abandonment cliff applies to business records.",
      },
      { type: "h2", text: "A gap is data, not a debt" },
      {
        type: "p",
        text: "The useful interpretation of a seven-day blank is not 'I failed.' It is 'this business did not get my attention for seven days.' That is a fact worth owning. Maybe another business ran hot. Maybe you were needed elsewhere. Maybe the daily move had become so small that it no longer felt worth logging. Whatever the cause, the gap belongs in the record as a gap. Do not invent fake entries to make the line continuous.",
      },
      {
        type: "quote",
        text: "The record is only useful if it reflects what actually happened, including the stretches where nothing happened.",
      },
      { type: "h2", text: "The re-entry ritual" },
      {
        type: "list",
        items: [
          "Do not backfill. One honest entry from today is worth more than seven invented ones.",
          "Read the last three to five real entries, not your inbox. Reconstruct the business's state from your own words.",
          "Write one move for today only. Do not try to compensate for the week with a heroic list.",
          "Leave the gap visible. The timeline should show the truth.",
        ],
      },
      { type: "h2", text: "If the gap is longer than a week" },
      {
        type: "p",
        text: "The same rule scales. Read the last ten entries. Note what was in motion before the pause. Decide whether the old priority is still the right one — a month away often changes the answer. Then write today's single move. If the business no longer deserves daily attention, demote it to maintenance or close it. The record helps you make that decision from evidence instead of guilt.",
      },
      { type: "h2", text: "How The Founders App handles gaps" },
      {
        type: "p",
        text: "There are no streaks, no broken-chain animations, and no catch-up screens. A missed week appears in the timeline as exactly what it is: a quiet stretch. When you return, the app asks the same question it asked before — what's one move you'll do today — and records the answer. The gap stays readable, and the next entry sits right after it.",
      },
    ],
  },
  {
    slug: "best-tools-solo-founders-multiple-businesses",
    title: "The best tools for solo founders running multiple businesses",
    metaTitle: "Best Apps for Solo Founders Running Multiple Businesses",
    description:
      "An honest shortlist of startup tools for solopreneurs: goal tracking, contacts, scheduling, focus, notes and finance — with real alternatives for each.",
    excerpt:
      "Running two or three ventures alone means every tool has to earn its place. A category-by-category shortlist of what actually works, with honest alternatives.",
    category: "Tools",
    published: "2026-08-27",
    readMinutes: 8,
    relatedSlugs: [
      "running-multiple-businesses-without-losing-the-thread",
      "founder-crm-contact-interaction-log",
      "one-thing-a-day-business-progress",
    ],
    blocks: [
      {
        type: "p",
        text: "Most \"best tools\" lists are written for teams, padded with affiliate links, and indifferent to whether you finish the year using any of them. This one is narrower: it is for one person running two or three ventures at once, where every subscription has to justify both its cost and its context-switching overhead. Nothing here is sponsored, and we include direct alternatives to our own product where they exist.",
      },
      {
        type: "p",
        text: "The selection rule is simple: a tool earns a slot if it works in under ten minutes of setup and still makes sense on a day when you touched the other business instead.",
      },
      { type: "h2", text: "Goal tracking and accountability" },
      {
        type: "p",
        text: "This is the category we build in, so judge this entry accordingly — but be honest with yourself about what you need: not a task manager, but a record of what actually moved.",
      },
      {
        type: "list",
        items: [
          "The Founders App — morning move, evening outcome, monthly bold move, per business. No streaks, no scores. Built specifically for the multi-venture case. Early access, no billing yet.",
          "Todoist — the default answer for a reason. Excellent capture and recurring tasks, but it measures what you planned, and everything lands in one undifferentiated list unless you discipline yourself with projects.",
          "A notebook and pen — genuinely competitive at this category. Zero cost, zero lock-in, and no notifications. Its failure mode is review: paper makes it hard to read your own month at a glance.",
        ],
      },
      { type: "h2", text: "Contact management" },
      {
        type: "p",
        text: "You do not need a sales CRM. You need to remember who someone is, what was said, and what you owe them, six months later. We wrote about why full pipeline software backfires for founders separately.",
      },
      {
        type: "list",
        items: [
          "The Founders App phonebook — a standing note plus a dated interaction log per person, scoped to each business so you never pitch the same contact twice from two different ventures.",
          "Clay — a polished personal CRM with strong enrichment. Worth it if relationship management is the core of your business; heavy if it is not.",
          "Apple/Google Contacts with a notes field — the floor of the category. Better than nothing, and the failure mode is that nobody actually opens the notes field.",
        ],
      },
      { type: "h2", text: "Scheduling" },
      {
        type: "list",
        items: [
          "Cal.com — open source, generous free tier, supports multiple calendars and event types. The one to start with.",
          "Calendly — the incumbent everyone recognises, so guests are never confused. Free tier covers one event type, which is often enough.",
          "Fantastical (Apple only) — if your problem is seeing your own week rather than letting others book into it.",
        ],
      },
      { type: "h2", text: "Focus and deep work" },
      {
        type: "p",
        text: "The mechanism matters less than the boundary: a start time, an end time, and a stated intention. Any timer that supports that works.",
      },
      {
        type: "list",
        items: [
          "The Founders App focus timer — a standalone timer with presets that lives next to the work it is for. Deliberately unexciting.",
          "Session — a well-made Pomodoro app for Apple platforms with proper analytics.",
          "Forest — grows a tree while you stay off your phone. Gamified, which either works on you or doesn't; you will know within a week.",
        ],
      },
      { type: "h2", text: "Notes and long-form thinking" },
      {
        type: "list",
        items: [
          "Obsidian — local Markdown files you own forever. The best long-term bet in the category if you can tolerate some setup.",
          "Apple Notes / Google Keep — fast capture, zero cost, and already on your phone. Search is weak but capture speed is unbeatable.",
          "Notion — powerful and genuinely free for personal use, but it invites system-building. Founders lose weeks designing a workspace instead of running the business. If that sentence stings, pick Obsidian.",
        ],
      },
      { type: "h2", text: "Money tracking" },
      {
        type: "list",
        items: [
          "A spreadsheet — honest answer first. For two or three small ventures, a sheet per business with monthly income and expense columns is enough for longer than people admit.",
          "Wave — free invoicing and accounting, aimed squarely at very small businesses.",
          "Xero or QuickBooks — when an accountant enters the picture, use whatever they prefer. That preference matters more than any feature comparison.",
        ],
      },
      { type: "h2", text: "The pattern worth noticing" },
      {
        type: "p",
        text: "Look at what is not on this list: no project-management suite, no all-in-one workspace, nothing that requires a weekly review ceremony to stay useful. A solo founder's stack should be boring, cheap, and legible at a glance. If a tool needs an afternoon of onboarding videos, it was built for a company you do not have.",
      },
      {
        type: "quote",
        text: "The right tool is the one that is still useful on the week you neglected it.",
      },
      {
        type: "p",
        text: "Start with one tool per category, chosen in an afternoon, and change only when something demonstrably hurts. The businesses are the project; the stack is not.",
      },
    ],
  },
  {
    slug: "what-it-takes-to-be-a-successful-founder",
    title: "What it takes to be a successful founder — and why most quit one project too early",
    metaTitle: "What It Takes to Be a Successful Founder (and Who Quits)",
    description:
      "What separates successful founders from the ones who abandon a project and start a new one: focus, finishing, a written record, and the discipline of staying with one bet long enough to learn from it.",
    excerpt:
      "The difference between founders who make it and founders who keep starting over is rarely talent. It is how long they can stay with one hard, boring, unglamorous bet — and whether they kept a record of it.",
    category: "Mindset",
    published: "2026-09-02",
    readMinutes: 9,
    relatedSlugs: [
      "monthly-bold-move-framework",
      "what-to-do-when-you-miss-a-week",
      "one-thing-a-day-business-progress",
    ],
    blocks: [
      {
        type: "p",
        text: "Ask ten people what it takes to be a successful founder and you will get ten flattering answers: vision, risk tolerance, hustle, grit. Watch a hundred founders over five years and you get a far less flattering one. The people who succeed are usually not the smartest or the boldest in the room. They are the ones who stayed with a single unglamorous bet long enough for compounding to show up — while everyone around them rotated to a new idea every eleven weeks.",
      },
      {
        type: "p",
        text: "This is not a piece about grinding harder. Quitting is often correct, and staying on a dead project out of pride has destroyed more founders than early exits ever did. The question worth answering is narrower and more useful: what actually separates the founder who moves on for good reasons from the one who moves on because the middle got boring?",
      },
      { type: "h2", text: "The real difference: quitting on evidence vs. quitting on feeling" },
      {
        type: "p",
        text: "Successful founders quit projects too. The difference is that they can tell you exactly why. They point at a number, a set of conversations, a pricing experiment that failed three ways. The serial restarter cannot — because there is no record to point at. The decision was made on a Tuesday when the work felt heavy and a new idea felt light.",
      },
      {
        type: "list",
        items: [
          "Quitting on evidence: \"I ran outreach for ninety days, talked to forty buyers, and eight percent of them had the problem I thought was universal. The market is too thin at this price.\"",
          "Quitting on feeling: \"It stopped being fun and I read a thread about a better space.\"",
          "Quitting on evidence has a written trail, a date range, and a repeatable lesson you carry to the next thing.",
          "Quitting on feeling produces the same failure again in a new costume, twelve months later.",
        ],
      },
      {
        type: "quote",
        text: "You cannot learn from a project you did not document. You can only repeat it.",
      },
      { type: "h2", text: "Seven traits successful founders actually share" },
      {
        type: "p",
        text: "Strip away the mythology and the same small set of behaviours keeps showing up in founders who eventually get somewhere — across bootstrapped agencies, small software products, coffee shops, and service businesses alike.",
      },
      {
        type: "list",
        items: [
          "They finish things. Not perfectly — visibly. A shipped, mediocre version teaches more in a week than a perfect plan teaches in a quarter.",
          "They keep one bet primary. Side projects exist, but one business gets the first hour of the day for a long stretch of months.",
          "They talk to buyers before they build. The successful ones are usually embarrassed by how simple their first offer was.",
          "They write things down. Decisions, prices tried, conversations had. Their memory of the business is external, not emotional.",
          "They take a deliberate risk on a schedule — a price rise, a cold pitch, a public launch — rather than waiting for courage to arrive.",
          "They recover from missed weeks without ceremony. No guilt spiral, no restart-from-zero, just the next entry.",
          "They measure attention, not activity. They know which category of work actually got their hours last month.",
        ],
      },
      { type: "h2", text: "Why the middle is where founders are lost" },
      {
        type: "p",
        text: "Every project has three phases. The beginning is euphoric: everything is possible and nothing is proven. The end is validating: revenue, users, a real thing. The middle is a long, flat corridor where the work is repetitive, the feedback is thin, and no one is clapping. Almost nobody quits at the beginning or the end. They quit in the corridor.",
      },
      {
        type: "p",
        text: "What makes the corridor survivable is not motivation — motivation is a weather pattern, not a strategy. It is visible evidence of movement. When you can scroll back and see sixty days of specific, dated moves you made, the corridor stops feeling static. You are not stuck; you are eight hundred small decisions deeper than you were in March. That single shift in perception is, in practice, the difference between a founder who continues and a founder who opens a new domain registrar tab.",
      },
      { type: "h2", text: "The new-project trap, and how it feels from the inside" },
      {
        type: "p",
        text: "Starting something new is genuinely intoxicating. There is no legacy code, no unhappy customer, no pricing mistake to unwind. The clean slate produces a chemical high that resembles progress closely enough to fool an experienced person. Notice the tell: the new idea almost always arrives at the exact moment the current project requires something uncomfortable — a sales call, a price increase, a hard conversation, a refund.",
      },
      {
        type: "list",
        items: [
          "You feel most creative about a new business precisely when the current one asks you to do outreach.",
          "The new idea is described in terms of how it will feel to work on, not who will pay for it.",
          "You cannot state, in one sentence, the experiment that would prove the current project dead.",
          "You have no written record of the last ninety days, so abandoning it costs nothing emotionally.",
        ],
      },
      {
        type: "p",
        text: "None of these mean the new idea is bad. They mean the decision is being made by mood. Write the kill criteria for the current project before you entertain the next one, and the choice becomes an operating decision rather than an escape.",
      },
      { type: "h2", text: "How to decide: a founder's kill-or-continue test" },
      {
        type: "p",
        text: "Before you move on, answer four questions in writing. If you cannot answer them, you have not yet earned the right to quit — not out of stubbornness, but because you would be discarding an unfinished experiment.",
      },
      {
        type: "list",
        items: [
          "What specifically did I try, and on what dates? Vague memory does not count.",
          "How many real buyer conversations happened, and what did they say verbatim?",
          "What was the single boldest move I made, and did I make one every month?",
          "If a stranger read my record of the last ninety days, would they say I tested the business or merely maintained it?",
        ],
      },
      {
        type: "quote",
        text: "Persistence is not staying forever. It is staying long enough to have an answer.",
      },
      { type: "h2", text: "Focus is a system, not a personality trait" },
      {
        type: "p",
        text: "Founders who stay focused are rarely more disciplined by nature. They have a structure that makes drifting obvious. A daily move keeps the business in motion when motivation is absent. A monthly bold move guarantees that the uncomfortable, high-leverage action happens on a schedule rather than on a feeling. A private journal keeps the emotional reality of the business separate from the operating record, so a bad week does not get mistaken for a bad business. A contact log means relationships compound instead of resetting. A single timeline turns effort into evidence.",
      },
      {
        type: "p",
        text: "Miss the structure and every project regresses to the same shape: bursts of enthusiasm, an unrecorded middle, and a quiet abandonment that teaches nothing.",
      },
      { type: "h2", text: "Why The Founders App was built for exactly this" },
      {
        type: "p",
        text: "The Founders App exists because of that corridor. It is a private, PIN-locked operating system for every business you run, and it is deliberately narrow: one move a day, one bold move a month, a journal nobody else reads, a contact log with memory, and one timeline that holds all of it. There are no streaks to break, no guilt notifications, no leaderboard, no AI writing your thoughts for you. Missing a week costs you nothing except a week.",
      },
      {
        type: "list",
        items: [
          "Daily move — keeps the business in motion during the flat middle, one specific action at a time.",
          "Monthly bold move — schedules the uncomfortable, high-leverage risk so courage is not a prerequisite.",
          "Private journal — separates how the week felt from how the business performed.",
          "Contact log — makes relationships compound across months instead of resetting each quarter.",
          "History timeline — the evidence you need to quit honestly or continue with conviction.",
          "Multi-business support — several ventures, each with its own record, so nothing bleeds together.",
        ],
      },
      {
        type: "p",
        text: "The purpose is not to make you work more. It is to make sure that when you eventually face the kill-or-continue question — and you will — you are answering it with a record instead of a mood. Founders who keep records move on for the right reasons. Founders who do not keep starting the same project with a different name.",
      },
      {
        type: "p",
        text: "Build the record for ninety days. Then decide. That, more than vision or risk tolerance, is what it takes.",
      },
    ],
  },
  {
    slug: "importance-of-setting-goals",
    title: "The importance of setting goals — and why most founder goals fail",
    metaTitle: "The Importance of Setting Goals for Founders",
    description:
      "Why setting goals matters for founders: vague intent drifts, specific goals create motion, and a written record turns intention into a business that moves.",
    excerpt:
      "Most founders set goals the wrong way: too many, too vague, or too public. Here's why specific, written goals matter — and how to keep them alive past the first week.",
    category: "Mindset",
    published: "2026-09-03",
    readMinutes: 7,
    relatedSlugs: [
      "one-thing-a-day-business-progress",
      "monthly-bold-move-framework",
      "what-it-takes-to-be-a-successful-founder",
    ],
    blocks: [
      {
        type: "p",
        text: "Most founders do not fail because they lack ambition. They fail because their goals are written to feel good, not to force a decision. \"Grow revenue\" sounds like a target. It is not. It is a permission slip to call almost anything progress. A dozen likes, a redesigned button, an answered email — all of it can be folded into \"growing revenue\" because the goal was never specific enough to say no.",
      },
      {
        type: "quote",
        text: "A goal you cannot fail at is not a goal. It is a mood.",
      },
      {
        type: "p",
        text: "The importance of setting goals is not motivational fluff. It is the difference between a business that compounds and one that merely stays busy. A real goal narrows the world. It tells you what to do this afternoon and, just as importantly, what not to do. That narrowing is uncomfortable, which is why most founders avoid it.",
      },
      { type: "h2", text: "Why vague goals are worse than no goals" },
      {
        type: "p",
        text: "No goal at least admits the truth: you are improvising. A vague goal gives you the feeling of direction without the cost of decision. It lets you end a day exhausted and still believe you moved forward, because nothing concrete was ever defined.",
      },
      {
        type: "list",
        items: [
          "Vague goal: 'grow the business this quarter.' No line to cross, so every day is a guess.",
          "Specific goal: 'secure three paying customers from outbound by March 31.' Now the work can be chosen, rejected, and reviewed.",
          "The specific version is scarier because it can fail in public. That is exactly why it works.",
        ],
      },
      {
        type: "p",
        text: "Specificity is a filter. It filters out the tasks that feel productive but do not matter. It filters out the meetings that should have been emails. It filters out the content that sounds good but produces no signal. A founder with a specific goal has a much easier time saying no — and saying no is most of the job.",
      },
      { type: "h2", text: "The three ways founders lie to themselves about goals" },
      {
        type: "list",
        items: [
          "They set too many. Three active goals is usually one too many. Everything competes for the same attention, and attention always loses.",
          "They confuse inputs with outcomes. 'Post three times a week' is an input. 'Generate five qualified leads from content' is an outcome. Inputs are easy to control; outcomes are what matter.",
          "They make goals public for the applause. Announcing a target can feel like progress itself. The likes substitute for the work, and the work never happens.",
        ],
      },
      {
        type: "quote",
        text: "The best goal is the one that changes what you do this afternoon.",
      },
      { type: "h2", text: "The research is not on the side of dreamers" },
      {
        type: "p",
        text: "Goal-setting research consistently finds that specific, challenging goals produce higher performance than vague \"do your best\" instructions. The mechanism is not magic. A specific goal narrows attention, increases effort, and makes feedback meaningful. \"Do your best\" lets you quit at 60 percent and still feel virtuous. A number does not.",
      },
      {
        type: "p",
        text: "For a founder, this means the daily work becomes answerable to something outside the moment. You stop asking \"what do I feel like doing?\" and start asking \"what moves the stated goal?\" That single reframe protects hours.",
      },
      { type: "h2", text: "Why written goals survive longer than mental ones" },
      {
        type: "p",
        text: "A goal held only in your head adapts to your mood. By Thursday afternoon it has quietly become less ambitious. By next week it has been replaced by a new one. Writing it down does two things: it creates a fixed reference point, and it makes the drift visible when you re-read it.",
      },
      {
        type: "list",
        items: [
          "Write the goal in one sentence. If it takes a paragraph, it is a wish list.",
          "Attach a date. A goal without a horizon is a preference.",
          "Define done. You should be able to answer yes or no on the deadline.",
          "Review it weekly, not just when you remember it.",
        ],
      },
      {
        type: "p",
        text: "The founders who actually hit their targets tend to have one absurdly simple habit: they look at the written goal regularly, compare it to what they did, and adjust from evidence rather than enthusiasm.",
      },
      { type: "h2", text: "Daily goals vs. monthly goals: both matter" },
      {
        type: "p",
        text: "A founder needs two speeds of goal. The daily goal keeps the business moving when motivation is low: one concrete action, recorded, reviewed each evening. The monthly goal handles the bigger, scarier bets that never feel urgent: raising prices, launching a partnership, killing a product line.",
      },
      {
        type: "p",
        text: "Daily goals without monthly goals produce a year of efficient mediocrity. Monthly goals without daily goals produce big intentions that never land. The two rhythms need to talk to each other, and they need a record to do it.",
      },
      { type: "h2", text: "Why goals need a record, not just a list" },
      {
        type: "p",
        text: "Task lists are about the future. Records are about the truth. A goal lives or dies by whether the work attached to it actually happened, and whether that work produced the expected signal. Without a record, you are left with the story your memory invents — usually rosier than reality.",
      },
      {
        type: "p",
        text: "The record lets you ask harder questions: did I actually do the outreach? Did I avoid the bold move three weeks in a row? Did the goal even make sense, or should it be killed? These questions are uncomfortable, but they are the entire point. A goal that cannot be questioned is a slogan.",
      },
      { type: "h2", text: "How The Founders App keeps goals alive" },
      {
        type: "p",
        text: "The Founders App was built around the idea that a goal should not live in a planner — it should live in the work. Every business gets its own daily loop: one morning intention tied to what matters right now, one evening outcome that records what actually happened, and a category tag so the month can be read.",
      },
      {
        type: "list",
        items: [
          "One daily move forces the goal into a single, actionable step.",
          "Evening outcome creates a written record of what the work produced.",
          "Monthly bold move keeps the uncomfortable, high-leverage goal on a schedule.",
          "Per-business history keeps each goal's record clean and separate.",
          "No streaks means a missed day does not destroy the goal — it just appears as a gap you can interpret.",
        ],
      },
      {
        type: "p",
        text: "The journal keeps emotional noise out of the operating record. The history timeline is the evidence you need to decide whether the goal is working. The PIN lock keeps the whole thing private, because some goals are not ready for an audience.",
      },
      {
        type: "p",
        text: "Setting goals is not the sexy part of running a business. It is the part that makes everything else matter. A clear goal turns a random Tuesday into progress. A written record turns a quarter into a story you can learn from. And a system that keeps the goal close to the work — without guilt, noise, or public performance — is the difference between founders who finish the year proud of what they built and founders who wonder where the time went.",
      },
      {
        type: "p",
        text: "If your goals keep evaporating, the problem is rarely discipline. It is usually that the goal has no daily address. Give it one.",
      },
    ],
  },
  {
    slug: "repetition-decides-who-you-become",
    title: "How repetition quietly decides who you become",
    metaTitle: "Repetition, Neuroplasticity and Founder Identity",
    description:
      "Neuroplasticity means your brain rewires around whatever you repeat. Founders can use that mechanism on purpose: one daily move, one monthly bold move, and a record that keeps the wiring honest.",
    excerpt:
      "Your brain is never finished. It reshapes itself around whatever you repeat. Here is how to use neuroplasticity as a founder instead of letting it use you.",
    category: "Mindset",
    published: "2026-09-03",
    readMinutes: 8,
    relatedSlugs: [
      "one-thing-a-day-business-progress",
      "importance-of-setting-goals",
      "monthly-bold-move-framework",
    ],
    blocks: [
      {
        type: "p",
        text: "Somewhere out there is a version of you that is calmer, more disciplined, better under pressure, and more confident in decisions. The gap between that person and you is not talent or luck. It is wiring. And wiring changes through repetition.",
      },
      {
        type: "p",
        text: "Neuroplasticity is the brain's ability to physically reorganize itself for your whole life. New connections form between neurons. Existing ones get stronger or weaker. Whole regions can grow or shrink depending on what you repeatedly do. This is not a motivational metaphor. It is measurable biology. London taxi drivers grow larger hippocampi. String players develop expanded sensory maps for their fingering fingers. Stroke patients rebuild motor function years after doctors said recovery had plateaued.",
      },
      { type: "h2", text: "Why this matters more for founders than most people" },
      {
        type: "p",
        text: "Founders live in a high-stakes repetition environment. Every day you make decisions under uncertainty, face rejection, manage cash anxiety, and choose between the urgent and the important. Whatever you repeat in that loop becomes automatic. The question is whether you are automating the right patterns.",
      },
      {
        type: "list",
        items: [
          "Repeat avoidance and you become someone who avoids hard conversations.",
          "Repeat reactive email triage and you become someone who never gets to the deep work.",
          "Repeat one deliberate move a day and you become someone whose business moves even on bad days.",
        ],
      },
      {
        type: "quote",
        text: "Neurons that fire together, wire together. Your habits are not preferences. They are physical pathways.",
      },
      { type: "h2", text: "The willpower muscle is real" },
      {
        type: "p",
        text: "Research on the anterior midcingulate cortex shows that this small brain region grows when you do things you do not want to do. Not the fun stuff. The uncomfortable stuff. Resisting a distraction, finishing the task you dread, making the call you have been avoiding. The friction is not a side effect of growth. It is the mechanism.",
      },
      {
        type: "p",
        text: "For a founder this reframes discipline completely. Confidence and grit are not fixed personality traits. They are muscles with a location, and they respond to resistance training like any other muscle. Every time you choose the hard right move over the easy distraction, you are literally growing the part of your brain that makes that choice easier next time.",
      },
      { type: "h2", text: "Struggle is the point, not the obstacle" },
      {
        type: "p",
        text: "Neuroscientist Lara Boyd's research shows that the more you struggle while practicing a skill, the more your brain physically changes. Comfortable repetition barely moves the needle. Practice that pushes past your current edge drives structural rewiring.",
      },
      {
        type: "p",
        text: "This is why easy daily check-ins often feel good but produce little. Checking a box is comfortable. Doing the one move that makes you slightly nervous is not. The app that only rewards showing up trains a different brain than the app that rewards doing the thing that matters.",
      },
      { type: "h2", text: "How to use neuroplasticity on purpose" },
      {
        type: "p",
        text: "The practical version is simple, but it is not a single heroic session. Frequency beats intensity. Short, consistent practice builds pathways better than one exhausted marathon a week. Five focused minutes daily will outbuild one three-hour slog.",
      },
      {
        type: "list",
        items: [
          "Load the resistance on purpose. Do the specific thing you do not want to do.",
          "Move before you learn. Brief intense movement primes acetylcholine and norepinephrine, the chemicals that open the window for new wiring.",
          "Protect your sleep. Structural rewiring gets locked in while you sleep.",
          "Interrupt the loops you do not want. Catching a rumination or self-doubt loop and redirecting it weakens it a little each time.",
          "Expect proportional timelines. A twenty-year pattern takes longer to unwind than a two-week habit. Both are possible.",
        ],
      },
      { type: "h2", text: "The uncomfortable truth about cutting both ways" },
      {
        type: "p",
        text: "Neuroplasticity has no moral compass. It strengthens whatever you repeat. Anxious spirals, procrastination, doom scrolling, and self-doubt all get faster and more automatic every time you run them. The same mechanism that grows a taxi driver's hippocampus is the mechanism that deepens a founder's fear of sales calls.",
      },
      {
        type: "quote",
        text: "The only thing you can truly control is where you place your attention and where you place your effort. That is the entire lever.",
      },
      { type: "h2", text: "Why a record is the missing piece" },
      {
        type: "p",
        text: "Repetition only works in your favor if you know what you are actually repeating. Memory is not a reliable witness. It rewrites the past to protect your self-image. A written record is the only way to see whether you are repeating bold moves or repeating excuses.",
      },
      {
        type: "p",
        text: "Without a record, a founder can believe they are persistent when they are actually just busy. They can believe they take risks when they actually avoid the same conversation for three months. The record makes the wiring visible.",
      },
      { type: "h2", text: "How The Founders App turns repetition into an asset" },
      {
        type: "p",
        text: "The Founders App was designed around the biology of repetition. It does not try to motivate you with streaks or gamification. It gives you a simple, private structure that makes the right repetitions easy to perform and easy to review.",
      },
      {
        type: "list",
        items: [
          "Daily move — one specific action per business, every working day. Small enough to repeat, specific enough to rewire.",
          "Evening outcome — a written record of what actually happened. This is where the loop closes and the pathway strengthens.",
          "Monthly bold move — a deliberately uncomfortable move, scheduled so courage is not required to remember it.",
          "Private journal — a place to process emotion without polluting the operating record.",
          "Contact log — repeated outreach becomes a relationship instead of a series of forgotten touches.",
          "History timeline — the evidence of what you have actually repeated, so you can decide what to keep and what to interrupt.",
        ],
      },
      {
        type: "p",
        text: "The app is not magic. It is a container for intentional repetition. The transformation happens in your brain, one recorded move at a time.",
      },
      {
        type: "h2",
        text: "The real takeaway",
      },
      {
        type: "p",
        text: "Your brain is being shaped right now by whatever you are repeating, on purpose or by accident. The version of you that is wealthier, calmer, and more disciplined is not a fantasy. It is the natural output of specific pathways getting strengthened over time through specific repeated behavior.",
      },
      {
        type: "p",
        text: "The tool was never missing. It has been running this whole time. The only real choice is whether the repetition is intentional. The Founders App exists to make it intentional — one move, one month, one business at a time.",
      },
    ],
  },
];


export const POSTS_BY_DATE = [...POSTS].sort((a, b) => (a.published < b.published ? 1 : -1));

export function getPost(slug: string): Post | undefined {
  return POSTS.find((p) => p.slug === slug);
}

export function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}
